import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Message,
  Select,
  Space,
  Typography,
} from '@arco-design/web-react';
import { adminCardsApi, adminSandboxApi } from '@/api';
import { dollarsToCents } from '@/utils/money';
import type { AdminCardItem } from '@/types/card';
import type {
  SimulateTransactionResult,
  SimulateTransactionStatus,
} from '@/types/sandbox';

const STATUS_OPTIONS: Array<{ value: SimulateTransactionStatus; label: string; hint: string }> = [
  { value: 'pending', label: 'pending — 授权待处理', hint: '产生 pending_trans_id，供后续 complete/refund 使用' },
  { value: 'confirmed', label: 'confirmed — 授权成功', hint: '直接成功授权' },
  { value: 'declined', label: 'declined — 授权拒绝', hint: '模拟拒付' },
  { value: 'complete', label: 'complete — 清算完成', hint: '需填写 pendingTransId（来自 pending）' },
  { value: 'reverse', label: 'reverse — 冲正', hint: '需填写 pendingTransId' },
  { value: 'refund', label: 'refund — 退款', hint: '需填写 pendingTransId' },
];

const NEEDS_PENDING: SimulateTransactionStatus[] = ['complete', 'reverse', 'refund'];

interface FormState {
  cardId: string;
  status: SimulateTransactionStatus;
  pendingTransId?: string;
  mcc?: string;
  amount?: number;
  currency?: string;
  reason: string;
}

export default function SandboxSimulateView() {
  const [form] = Form.useForm<FormState>();
  const [cards, setCards] = useState<AdminCardItem[]>([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<SimulateTransactionResult | null>(null);
  const [lastPendingId, setLastPendingId] = useState<string | null>(null);

  const status = Form.useWatch('status', form) as SimulateTransactionStatus | undefined;

  const loadCards = useCallback(() => {
    setCardsLoading(true);
    adminCardsApi
      .list({ page: 1, pageSize: 100, status: 'ACTIVE' })
      .then((res) => setCards(res.items))
      .catch(() => setCards([]))
      .finally(() => setCardsLoading(false));
  }, []);

  useEffect(() => {
    loadCards();
    form.setFieldsValue({
      status: 'pending',
      currency: 'USD',
    });
  }, [loadCards, form]);

  const cardOptions = useMemo(
    () =>
      cards.map((c) => ({
        value: c.id,
        label: `${c.panMasked} · ${c.customerUsername}${c.upstreamCardId ? ` · ${c.upstreamCardId}` : ''}`,
        disabled: !c.upstreamCardId,
      })),
    [cards],
  );

  const handleSubmit = async () => {
    const values = await form.validate();
    setSubmitting(true);
    try {
      const result = await adminSandboxApi.simulateTransaction({
        cardId: values.cardId,
        status: values.status,
        pendingTransId: values.pendingTransId?.trim() || undefined,
        mcc: values.mcc?.trim() || undefined,
        transactionAmountCents:
          values.amount != null && values.amount > 0
            ? dollarsToCents(values.amount)
            : undefined,
        transactionCurrency: values.currency?.trim().toUpperCase() || undefined,
        reason: values.reason.trim(),
      });
      setLastResult(result);
      if (values.status === 'pending' && result.upstreamStatus === 'success') {
        const pendingFromResult = extractPendingTransId(result.upstreamResult);
        if (pendingFromResult) {
          setLastPendingId(pendingFromResult);
          form.setFieldValue('pendingTransId', pendingFromResult);
          Message.success(`模拟成功，pending_trans_id: ${pendingFromResult}`);
        } else {
          Message.success('模拟请求已提交（请从上游响应或 Webhook 收件箱获取 pending_trans_id）');
        }
      } else if (result.upstreamStatus === 'success') {
        Message.success('模拟交易成功');
      } else {
        Message.warning(
          result.upstreamMessage || `上游返回 ${result.upstreamStatus} (code=${result.upstreamCode})`,
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div>
          <div className="admin-page__title">沙箱 · 模拟交易</div>
          <div className="admin-page__subtitle">
            Newgen Issuing Guide §24 — 仅 sandbox 环境可用，用于测试已发卡片的授权/清算/退款流程
          </div>
        </div>
      </div>

      <Alert
        type="warning"
        style={{ marginBottom: 16 }}
        content={
          <div>
            <div>
              后端需配置 <Typography.Text code>APP_ENV=sandbox</Typography.Text> 且 Newgen 地址为沙箱。
            </div>
            <div style={{ marginTop: 4 }}>
              推荐流程：<Typography.Text bold>pending</Typography.Text> → 记录 pending_trans_id →{' '}
              <Typography.Text bold>complete</Typography.Text> 或 <Typography.Text bold>refund</Typography.Text>。
              沙箱商户模拟交易上限 200 笔。
            </div>
          </div>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 16, alignItems: 'start' }}>
        <Card title="提交模拟" className="admin-card">
          <Form form={form} layout="vertical" onSubmit={handleSubmit}>
            <Form.Item
              label="目标卡片（本地 ID，需已有上游 card_id）"
              field="cardId"
              rules={[{ required: true, message: '请选择卡片' }]}
            >
              <Select
                placeholder="选择 ACTIVE 卡片"
                loading={cardsLoading}
                showSearch
                filterOption={(input, option) =>
                  String(option?.props?.children ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={cardOptions}
                notFoundContent={cardsLoading ? '加载中…' : '暂无 ACTIVE 卡片，请先在客户台开卡'}
              />
            </Form.Item>

            <Form.Item label="交易状态 status" field="status" rules={[{ required: true }]}>
              <Select>
                {STATUS_OPTIONS.map((o) => (
                  <Select.Option key={o.value} value={o.value}>
                    {o.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            {status && (
              <Typography.Paragraph type="secondary" style={{ marginTop: -8, marginBottom: 12 }}>
                {STATUS_OPTIONS.find((o) => o.value === status)?.hint}
              </Typography.Paragraph>
            )}

            <Form.Item
              label="pending_trans_id"
              field="pendingTransId"
              rules={
                status && NEEDS_PENDING.includes(status)
                  ? [{ required: true, message: 'complete / reverse / refund 必须填写' }]
                  : undefined
              }
              extra={
                lastPendingId ? (
                  <Button
                    type="text"
                    size="mini"
                    onClick={() => form.setFieldValue('pendingTransId', lastPendingId)}
                  >
                    填入上次 pending: {lastPendingId}
                  </Button>
                ) : undefined
              }
            >
              <Input placeholder="先执行 pending 后从响应或 Webhook 获取" allowClear />
            </Form.Item>

            <Form.Item label="MCC（可选）" field="mcc">
              <Input placeholder="如 5411" maxLength={8} allowClear />
            </Form.Item>

            <Space style={{ width: '100%' }} align="start">
              <Form.Item
                label="交易金额"
                field="amount"
                style={{ flex: 1 }}
                rules={[
                  {
                    validator: (val, cb) => {
                      if (val != null && val <= 0) cb('金额须大于 0');
                      else cb();
                    },
                  },
                ]}
              >
                <InputNumber min={0.01} precision={2} step={1} style={{ width: '100%' }} placeholder="10.00" />
              </Form.Item>
              <Form.Item label="币种" field="currency" style={{ width: 120 }}>
                <Input placeholder="USD" maxLength={3} />
              </Form.Item>
            </Space>

            <Form.Item
              label="操作原因（审计）"
              field="reason"
              rules={[
                { required: true, message: '请填写原因' },
                { minLength: 4, message: '至少 4 个字符' },
              ]}
            >
              <Input.TextArea placeholder="如：sandbox 授权测试" autoSize={{ minRows: 2, maxRows: 4 }} />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting}>
                调用模拟接口
              </Button>
              <Button style={{ marginLeft: 8 }} onClick={loadCards}>
                刷新卡片列表
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <Card title="最近一次响应" className="admin-card">
          {lastResult ? (
            <Descriptions
              column={1}
              size="small"
              data={[
                { label: '卡号', value: lastResult.panMasked },
                { label: '上游 card_id', value: lastResult.upstreamCardId },
                { label: '模拟 status', value: lastResult.status },
                { label: '上游 status', value: lastResult.upstreamStatus },
                { label: '上游 code', value: String(lastResult.upstreamCode ?? '-') },
                { label: '上游 message', value: lastResult.upstreamMessage || '-' },
                {
                  label: 'upstream result',
                  value: (
                    <pre className="admin-mono" style={{ fontSize: 11, whiteSpace: 'pre-wrap', margin: 0 }}>
                      {JSON.stringify(lastResult.upstreamResult, null, 2)}
                    </pre>
                  ),
                },
              ]}
            />
          ) : (
            <Typography.Text type="secondary">提交后将显示上游返回摘要</Typography.Text>
          )}
        </Card>
      </div>
    </div>
  );
}

function extractPendingTransId(result: unknown): string | null {
  if (!result) return null;
  if (Array.isArray(result)) {
    for (const item of result) {
      const id = pickPendingId(item);
      if (id) return id;
    }
    return null;
  }
  return pickPendingId(result);
}

function pickPendingId(obj: unknown): string | null {
  if (!obj || typeof obj !== 'object') return null;
  const r = obj as Record<string, unknown>;
  const keys = ['pending_trans_id', 'pendingTransId', 'trans_id', 'transaction_id'];
  for (const k of keys) {
    const v = r[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}